import React, { ReactNode } from 'react';
import DropzoneBody from 'src/components/DropzoneBody/DropzoneBody';
import FileChip from 'src/pages/Property/HomeOwnerImportDialogue/Components/FileChip/FileChip';
import Loader from 'src/components/Loader';
import ColumnValidationCard from 'src/pages/Property/HomeOwnerImportDialogue/Components/ColumnValidationCard/ColumnValidationCard';
import { ColumnValidationResult } from 'src/utils/CommonHelpers/sharedCSVUtils';
import ImportSuccessBody from 'src/pages/Property/HomeOwnerImportDialogue/Components/ImportSuccessBody/ImportSuccessBody';
import './GenericDialogueBody.scss';

interface ImportingBodyProps {
  count: number;
  entityName: string;
}

const ImportingBody = ({ count, entityName }: ImportingBodyProps) => (
  <div className="csv-import_importing">
    <Loader center={true} />
    <div className="csv-import_importing_label">
      Importing {count} {entityName}{count !== 1 ? 's' : ''}…
    </div>
  </div>
);

interface FileLoadedBodyProps {
  file: File;
  validationStatus: 'idle' | 'validating' | 'valid' | 'invalid';
  columnValidation: ColumnValidationResult;
  recordCount: number;
  entityName: string;
  hasRowErrors: boolean;
  isImportError: boolean;
  apiError: string | null;
  requiredColumns: string[];
  optionalColumns: string[];
  optionalMissingColumns: string[];
  onRemove: () => void;
  children?: ReactNode;
}

const FileLoadedBody = ({
  file,
  validationStatus,
  columnValidation,
  recordCount,
  entityName,
  hasRowErrors,
  isImportError,
  apiError,
  requiredColumns,
  optionalColumns,
  optionalMissingColumns,
  onRemove,
  children,
}: FileLoadedBodyProps) => (
  <>
    <FileChip file={file} onRemove={onRemove} />
    {validationStatus === 'validating' && (
      <div className="csv-import_validating">
        <Loader center={true} />
        <div className="csv-import_validating_label">Reading and validating CSV…</div>
      </div>
    )}
    {(validationStatus === 'valid' || validationStatus === 'invalid') && (
      <ColumnValidationCard
        columnValidation={columnValidation}
        hasRowErrors={hasRowErrors}
        requiredColumns={requiredColumns}
        optionalColumns={optionalColumns}
        optionalMissingColumns={optionalMissingColumns}
      />
    )}
    {children}
    {validationStatus === 'valid' && (
      <div className="csv-import_record_count">
        {recordCount} {entityName}{recordCount !== 1 ? 's' : ''} detected
      </div>
    )}
    {isImportError && apiError && (
      <div className="csv-import_api_error">
        <div className="csv-import_api_error_title">Import failed</div>
        <div className="csv-import_api_error_message">{apiError}</div>
      </div>
    )}
  </>
);

interface DialogueBodyProps {
  isSuccess: boolean;
  isImporting: boolean;
  file: File | null;
  importResult: { totalRecords: number } | null;
  recordCount: number;
  validationStatus: 'idle' | 'validating' | 'valid' | 'invalid';
  columnValidation: ColumnValidationResult;
  hasRowErrors?: boolean;
  isImportError: boolean;
  apiError: string | null;
  entityName?: string;
  successMessage?: string;
  requiredColumns: string[];
  optionalColumns?: string[];
  optionalMissingColumns?: string[];
  getRootProps: () => object;
  getInputProps: () => object;
  open: () => void;
  reset: () => void;
  children?: ReactNode;
}

const DialogueBody = ({
  isSuccess,
  isImporting,
  file,
  importResult,
  recordCount,
  validationStatus,
  columnValidation,
  hasRowErrors = false,
  isImportError,
  apiError,
  entityName = 'record',
  successMessage,
  requiredColumns,
  optionalColumns = [],
  optionalMissingColumns = [],
  getRootProps,
  getInputProps,
  open,
  reset,
  children,
}: DialogueBodyProps) => {
  if (isSuccess) {
    return (
      <ImportSuccessBody
        importResult={importResult}
        entityName={entityName}
        successMessage={successMessage}
      />
    );
  }

  if (isImporting) {
    return <ImportingBody count={recordCount} entityName={entityName} />;
  }

  if (!file) {
    return (
      <DropzoneBody
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        open={open}
      />
    );
  }

  return (
    <FileLoadedBody
      file={file}
      validationStatus={validationStatus}
      columnValidation={columnValidation}
      recordCount={recordCount}
      entityName={entityName}
      hasRowErrors={hasRowErrors}
      isImportError={isImportError}
      apiError={apiError}
      requiredColumns={requiredColumns}
      optionalColumns={optionalColumns}
      optionalMissingColumns={optionalMissingColumns}
      onRemove={reset}
    >
      {children}
    </FileLoadedBody>
  );
};

export default DialogueBody;